import React, { useCallback, useEffect, useState } from 'react';
import { ScrollText, Filter } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { Card } from '../../components/ui';
import { adminApi, AdminAuditEntry } from '../../api';

// Friendly labels for the known action slugs.
const ACTION_LABELS: Record<string, string> = {
  admin_bank_reveal: 'Bank details revealed',
  admin_withdrawal_release: 'Withdrawal released early',
  admin_withdrawal_approve: 'Withdrawal approved',
  admin_withdrawal_complete: 'Payout marked sent',
  admin_game_reopen: 'Game reopened',
  admin_game_approve: 'Dispute approved',
  user_set_verified: 'Verification changed',
  user_set_admin: 'Admin access changed',
  user_resend_verification: 'Verification email resent',
  user_email_sent: 'Email sent to user',
};

const FILTERS: { label: string; value: string }[] = [
  { label: 'All actions', value: '' },
  { label: 'Bank reveals', value: 'admin_bank_reveal' },
  { label: 'Payouts completed', value: 'admin_withdrawal_complete' },
  { label: 'Payouts approved', value: 'admin_withdrawal_approve' },
  { label: 'Emails sent', value: 'user_email_sent' },
  { label: 'Admin changes', value: 'user_set_admin' },
];

const AdminAudit: React.FC = () => {
  const [entries, setEntries] = useState<AdminAuditEntry[]>([]);
  const [action, setAction] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (a: string) => {
    setLoading(true);
    try { setEntries(await adminApi.getAudit(a ? { action: a, limit: 200 } : { limit: 200 })); }
    catch { setEntries([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(action); }, [action, load]);

  return (
    <AdminLayout>
      <div className="flex items-center gap-2 mb-3">
        <ScrollText className="h-5 w-5 text-accent-400" />
        <h1 className="text-lg font-bold text-white">Audit log</h1>
      </div>

      <div className="relative mb-3">
        <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <select
          value={action}
          onChange={(e) => setAction(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-800"
        >
          {FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-green-200/80 py-10 text-center">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="bg-white rounded-2xl p-8 text-center text-gray-500">No audit entries yet.</div>
      ) : (
        <div className="space-y-2">
          {entries.map((e) => (
            <Card key={e.id} className="p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 text-sm">{ACTION_LABELS[e.action] || e.action}</p>
                  {e.detail && <p className="text-xs text-gray-600 mt-0.5 break-words">{e.detail}</p>}
                  <p className="text-xs text-gray-400 mt-0.5">
                    {e.actor_email || 'system'}
                    {e.target_type ? ` · ${e.target_type} #${e.target_id}` : ''}
                  </p>
                </div>
                <p className="text-xs text-gray-400 whitespace-nowrap shrink-0">
                  {new Date(e.created_at).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })}
                </p>
              </div>
            </Card>
          ))}
        </div>
      )}
      <p className="text-xs text-green-300/60 mt-3 text-center">Newest first · up to 200 entries.</p>
    </AdminLayout>
  );
};

export default AdminAudit;
